import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/services/api';
import { imageUploadService } from '@/services/imageUploadService';

interface Photo {
  id: string;
  title: string;
  description?: string;
  image_url: string;
  thumbnail_url?: string;
  category: 'general' | 'events' | 'maintenance' | 'celebrations';
  tags: string[];
  sort_order: number;
  is_featured: boolean;
  is_active: boolean;
  created_at: string;
}

const PhotoGalleryManagementPage = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState<Photo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Array<{
    file: File;
    progress: number;
    status: 'uploading' | 'success' | 'error';
    error?: string;
  }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general' as const,
    tags: '',
    is_featured: false,
    is_active: true,
    image_url: '',
    thumbnail_url: ''
  });

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/photo-gallery');
      const photosData = response.data.data || [];
      // Ensure tags is always an array
      const photosWithTags = photosData.map((photo: any) => ({
        ...photo,
        tags: photo.tags || []
      }));
      setPhotos(photosWithTags);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    
    // Validate all files
    const invalidFiles = fileArray.filter(file => {
      const validation = imageUploadService.validateImage(file);
      return !validation.valid;
    });

    if (invalidFiles.length > 0) {
      alert(`Some files are invalid. Please ensure all files are images and under 10MB.`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Initialize upload tracking
    const initialUploads = fileArray.map(file => ({
      file,
      progress: 0,
      status: 'uploading' as const
    }));
    setUploadingFiles(initialUploads);
    setUploading(true);

    // Simulate progress updates
    let progressInterval: NodeJS.Timeout | null = null;
    progressInterval = setInterval(() => {
      setUploadingFiles(prev => prev.map(upload => {
        if (upload.status === 'uploading' && upload.progress < 90) {
          return { ...upload, progress: Math.min(upload.progress + 10, 90) };
        }
        return upload;
      }));
    }, 200);

    try {
      // Upload all images
      const uploadResults = await imageUploadService.uploadMultipleImages(
        fileArray,
        'general',
        true
      );

      if (progressInterval) {
        clearInterval(progressInterval);
      }

      // Update upload status to 100% and set final status
      const updatedUploads = initialUploads.map((upload, index) => {
        const result = uploadResults[index];
        return {
          ...upload,
          progress: 100,
          status: result.success ? 'success' as const : 'error' as const,
          error: result.success ? undefined : result.message
        };
      });
      setUploadingFiles(updatedUploads);

      // Create photo entries for successfully uploaded images
      const successfulUploads = uploadResults.filter(r => r.success);
      if (successfulUploads.length > 0) {
        const photoPromises = successfulUploads.map(async (result, index) => {
          const originalFile = fileArray[uploadResults.findIndex(r => r === result)];
          const photoData = {
            title: originalFile.name.replace(/\.[^/.]+$/, '') || `Photo ${Date.now()}`,
            description: formData.description || '',
            category: formData.category,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
            is_featured: formData.is_featured,
            is_active: formData.is_active,
            image_url: result.url,
            thumbnail_url: result.url // Use same URL for thumbnail, backend can generate if needed
          };

          try {
            await api.post('/photo-gallery', photoData);
            return { success: true };
          } catch (error) {
            console.error('Error creating photo entry:', error);
            return { success: false, error };
          }
        });

        await Promise.all(photoPromises);
        
        // Refresh photos list
        await fetchPhotos();
        
        // Reset form and close modal if it was open
        resetForm();
        setShowModal(false);
        
        // Don't auto-close, let user close manually after seeing results
      } else {
        alert('Failed to upload all images. Please try again.');
        setUploadingFiles([]);
        setUploading(false);
      }
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      console.error('Error uploading files:', error);
      
      // Mark all as error
      setUploadingFiles(prev => prev.map(upload => ({
        ...upload,
        progress: 100,
        status: 'error' as const,
        error: error instanceof Error ? error.message : 'Upload failed'
      })));
      
      // Don't clear immediately, let user see the errors
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // If editing, update the photo
    if (editingPhoto) {
      try {
        const photoData = {
          ...formData,
          tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        };
        await api.put(`/photo-gallery/${editingPhoto.id}`, photoData);
        setShowModal(false);
        setEditingPhoto(null);
        resetForm();
        fetchPhotos();
      } catch (error) {
        console.error('Error updating photo:', error);
        alert('Failed to update photo. Please try again.');
      }
    } else {
      // For new photos, if image_url is set, create single photo entry
      if (formData.image_url) {
        try {
          const photoData = {
            ...formData,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
          };
          await api.post('/photo-gallery', photoData);
          setShowModal(false);
          setEditingPhoto(null);
          resetForm();
          fetchPhotos();
        } catch (error) {
          console.error('Error saving photo:', error);
          alert('Failed to save photo. Please try again.');
        }
      } else {
        alert('Please select at least one photo to upload.');
      }
    }
  };

  const handleEdit = (photo: Photo) => {
    setEditingPhoto(photo);
    setFormData({
      title: photo.title,
      description: photo.description || '',
      category: photo.category,
      tags: photo.tags.join(', '),
      is_featured: photo.is_featured,
      is_active: photo.is_active,
      image_url: photo.image_url,
      thumbnail_url: photo.thumbnail_url || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await api.delete(`/photo-gallery/${id}`);
        fetchPhotos();
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'general',
      tags: '',
      is_featured: false,
      is_active: true,
      image_url: '',
      thumbnail_url: ''
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'events': return 'bg-blue-100 text-blue-800';
      case 'celebrations': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'general': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading photos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Upload Loading Overlay */}
      {uploading && uploadingFiles.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploading Photos</h3>
            <div className="space-y-3">
              {uploadingFiles.map((upload, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 truncate flex-1 mr-2">{upload.file.name}</span>
                    <span className={`text-xs font-medium whitespace-nowrap ${
                      upload.status === 'success' ? 'text-green-600' :
                      upload.status === 'error' ? 'text-red-600' :
                      'text-blue-600'
                    }`}>
                      {upload.status === 'success' ? '✓ Uploaded' :
                       upload.status === 'error' ? '✗ Failed' :
                       'Uploading...'}
                    </span>
                  </div>
                  <div className="bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        upload.status === 'success' ? 'bg-green-600' :
                        upload.status === 'error' ? 'bg-red-600' :
                        'bg-blue-600 animate-pulse'
                      }`}
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                  {upload.error && (
                    <p className="text-xs text-red-600">{upload.error}</p>
                  )}
                </div>
              ))}
            </div>
            {uploadingFiles.every(u => u.status === 'success' || u.status === 'error') && (
              <button
                onClick={() => {
                  setUploadingFiles([]);
                  setUploading(false);
                }}
                className="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Photo Gallery Management</h1>
              <p className="mt-2 text-lg text-gray-600">
                Upload and manage photos for the society gallery
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setEditingPhoto(null);
                setShowModal(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upload New Photo
            </button>
          </div>
        </div>

        {/* Photos Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {photos.map((photo) => (
            <div key={photo.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="aspect-square relative">
                <img
                  src={photo.thumbnail_url || photo.image_url}
                  alt={photo.title}
                  className="w-full h-full object-cover"
                />
                {photo.is_featured && (
                  <div className="absolute top-2 right-2">
                    <span className="bg-yellow-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                      Featured
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 hover:opacity-100 transition-opacity duration-300 flex space-x-2">
                    <button
                      onClick={() => handleEdit(photo)}
                      className="bg-white text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(photo.id)}
                      className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`${getCategoryColor(photo.category)} text-xs font-medium px-2 py-1 rounded-full`}>
                    {photo.category}
                  </span>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${photo.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {photo.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate">{photo.title}</h3>
                {photo.description && (
                  <p className="text-xs text-gray-600 line-clamp-2">{photo.description}</p>
                )}
                {photo.tags && photo.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {photo.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                    {photo.tags.length > 3 && (
                      <span className="text-xs text-gray-500">+{photo.tags.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Upload Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingPhoto ? 'Edit Photo' : 'Upload New Photo'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        {formData.image_url ? (
                          <div className="relative">
                            <img
                              src={formData.thumbnail_url || formData.image_url}
                              alt="Preview"
                              className="mx-auto h-32 w-32 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, image_url: '', thumbnail_url: '' }));
                                if (fileInputRef.current) fileInputRef.current.value = '';
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div>
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div className="flex text-sm text-gray-600">
                              <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                <span>Upload a file</span>
                                <input
                                  ref={fileInputRef}
                                  id="file-upload"
                                  name="file-upload"
                                  type="file"
                                  className="sr-only"
                                  accept="image/*"
                                  multiple
                                  onChange={handleFileSelect}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB (Multiple files supported)</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {uploading && uploadingFiles.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <p className="text-sm font-medium text-gray-700">Uploading {uploadingFiles.length} photo(s)...</p>
                        {uploadingFiles.map((upload, index) => (
                          <div key={index} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-700 truncate flex-1 mr-2">{upload.file.name}</span>
                              <span className={`text-xs font-medium ${
                                upload.status === 'success' ? 'text-green-600' :
                                upload.status === 'error' ? 'text-red-600' :
                                'text-blue-600'
                              }`}>
                                {upload.status === 'success' ? '✓ Uploaded' :
                                 upload.status === 'error' ? '✗ Failed' :
                                 'Uploading...'}
                              </span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  upload.status === 'success' ? 'bg-green-600' :
                                  upload.status === 'error' ? 'bg-red-600' :
                                  'bg-blue-600'
                                }`}
                                style={{ width: `${upload.progress}%` }}
                              />
                            </div>
                            {upload.error && (
                              <p className="text-xs text-red-600">{upload.error}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Title</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="general">General</option>
                        <option value="events">Events</option>
                        <option value="celebrations">Celebrations</option>
                        <option value="maintenance">Maintenance</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                      placeholder="society, event, celebration"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Featured Photo</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!formData.image_url || uploading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {editingPhoto ? 'Update' : 'Upload'} Photo
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoGalleryManagementPage;
